import React, { useState, useRef } from 'react';
import { extractDocumentData, isAnthropicConfigured, type ExtractedDocData } from '../services/documentImportService';

interface ReviewForm {
  doc_type:               'mandant' | 'arbeitnehmer' | 'unbekannt';
  firma:                  string;
  kurz_code:              string;
  strasse:                string;
  plz:                    string;
  ort:                    string;
  land:                   string;
  telefon:                string;
  email:                  string;
  branche:                string;
  mitarbeiterzahl:        string;
  ansprechperson:         string;
  ansprechperson_titel:   string;
  ansprechperson_email:   string;
  ansprechperson_telefon: string;
  person_vorname:         string;
  person_nachname:        string;
  person_geburtsdatum:    string;
  notizen:                string;
}

const EMPTY_FORM: ReviewForm = {
  doc_type: 'mandant', firma: '', kurz_code: '', strasse: '', plz: '', ort: '',
  land: 'Österreich', telefon: '', email: '', branche: '', mitarbeiterzahl: '',
  ansprechperson: '', ansprechperson_titel: '', ansprechperson_email: '',
  ansprechperson_telefon: '', person_vorname: '', person_nachname: '',
  person_geburtsdatum: '', notizen: '',
};

function toReviewForm(d: ExtractedDocData): ReviewForm {
  return {
    doc_type:               d.doc_type,
    firma:                  d.firma                  ?? '',
    kurz_code:              d.kurz_code              ?? '',
    strasse:                d.strasse                ?? '',
    plz:                    d.plz                    ?? '',
    ort:                    d.ort                    ?? '',
    land:                   d.land                   ?? 'Österreich',
    telefon:                d.telefon                ?? '',
    email:                  d.email                  ?? '',
    branche:                d.branche                ?? '',
    mitarbeiterzahl:        d.mitarbeiterzahl != null ? String(d.mitarbeiterzahl) : '',
    ansprechperson:         d.ansprechperson         ?? '',
    ansprechperson_titel:   d.ansprechperson_titel   ?? '',
    ansprechperson_email:   d.ansprechperson_email   ?? '',
    ansprechperson_telefon: d.ansprechperson_telefon ?? '',
    person_vorname:         d.person_vorname         ?? '',
    person_nachname:        d.person_nachname        ?? '',
    person_geburtsdatum:    d.person_geburtsdatum    ?? '',
    notizen:                d.notizen                ?? '',
  };
}

function fromReviewForm(f: ReviewForm): ExtractedDocData {
  return {
    doc_type:               f.doc_type,
    ...(f.firma                  && { firma:                  f.firma }),
    ...(f.kurz_code              && { kurz_code:              f.kurz_code }),
    ...(f.strasse                && { strasse:                f.strasse }),
    ...(f.plz                    && { plz:                    f.plz }),
    ...(f.ort                    && { ort:                    f.ort }),
    ...(f.land                   && { land:                   f.land }),
    ...(f.telefon                && { telefon:                f.telefon }),
    ...(f.email                  && { email:                  f.email }),
    ...(f.branche                && { branche:                f.branche }),
    ...(f.mitarbeiterzahl        && { mitarbeiterzahl:        parseInt(f.mitarbeiterzahl) || null }),
    ...(f.ansprechperson         && { ansprechperson:         f.ansprechperson }),
    ...(f.ansprechperson_titel   && { ansprechperson_titel:   f.ansprechperson_titel }),
    ...(f.ansprechperson_email   && { ansprechperson_email:   f.ansprechperson_email }),
    ...(f.ansprechperson_telefon && { ansprechperson_telefon: f.ansprechperson_telefon }),
    ...(f.person_vorname         && { person_vorname:         f.person_vorname }),
    ...(f.person_nachname        && { person_nachname:        f.person_nachname }),
    ...(f.person_geburtsdatum    && { person_geburtsdatum:    f.person_geburtsdatum }),
    ...(f.notizen                && { notizen:                f.notizen }),
  };
}

function EditField({ label, value, onChange, placeholder }: {
  label:        string;
  value:        string;
  onChange:     (v: string) => void;
  placeholder?: string;
}): React.JSX.Element {
  return (
    <div className="space-y-0.5">
      <label className="label-text">{label}</label>
      <input type="text" value={value} placeholder={placeholder}
        onChange={e => { onChange(e.target.value); }}
        className="input-field text-sm" />
    </div>
  );
}

type Step = 'idle' | 'dsgvo' | 'processing' | 'review' | 'error';

interface Props {
  onClose:  () => void;
  onImport: (data: ExtractedDocData) => void;
}

export function DocumentImportModal({ onClose, onImport }: Props): React.JSX.Element {
  const [step,     setStep]     = useState<Step>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [file,     setFile]     = useState<File | null>(null);
  const [form,     setForm]     = useState<ReviewForm>(EMPTY_FORM);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = (key: keyof ReviewForm) => (val: string): void => {
    setForm(prev => ({ ...prev, [key]: val }));
  };

  const handleFile = (f: File): void => {
    setFile(f);
    setStep('dsgvo');
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f !== undefined) handleFile(f);
  };

  const handleProcess = async (): Promise<void> => {
    if (file === null) return;
    if (!isAnthropicConfigured()) {
      setErrorMsg('Kein API Key konfiguriert. Bitte in Einstellungen → KI-Assistent hinterlegen.');
      setStep('error');
      return;
    }
    setStep('processing');
    try {
      const extracted = await extractDocumentData(file);
      setForm(toReviewForm(extracted));
      setStep('review');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setStep('error');
    }
  };

  const handleConfirm = (): void => {
    onImport(fromReviewForm(form));
    onClose();
  };

  const reset = (): void => {
    setStep('idle');
    setFile(null);
    setForm(EMPTY_FORM);
    setErrorMsg('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            📄 Dokument importieren
          </h2>
          <button onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* ── Drop Zone ── */}
          {step === 'idle' && (
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => { setDragOver(false); }}
              onClick={() => { fileInputRef.current?.click(); }}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors select-none
                ${dragOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/20'
                }`}
            >
              <p className="text-4xl mb-3">📁</p>
              <p className="font-medium text-gray-700 dark:text-gray-300">Datei hier ablegen</p>
              <p className="text-sm text-gray-400 mt-1">oder klicken zum Auswählen</p>
              <p className="text-xs text-gray-400 mt-3">
                JPG · PNG · WEBP · PDF (auch gescannt &amp; handschriftlich)
              </p>
              <input ref={fileInputRef} type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f !== undefined) handleFile(f);
                }} />
            </div>
          )}

          {/* ── DSGVO-Hinweis ── */}
          {step === 'dsgvo' && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                  ⚠️ DSGVO-Hinweis
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Das Dokument wird zur Analyse an die Anthropic API gesendet.
                </p>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  Bitte nur administrative Dokumente senden — Firmenkontakte, Visitenkarten,
                  Dienstzettel. Keine medizinischen Befunde, Diagnosen oder Patientendaten.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3 flex items-center gap-3">
                <span className="text-2xl flex-shrink-0">📄</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {file?.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {file !== null ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enthält das Dokument ausschließlich administrative Daten (keine Patientendaten)?
              </p>
            </div>
          )}

          {/* ── Processing ── */}
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-12 h-12 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Analysiere Dokument…
              </p>
              <p className="text-xs text-gray-400">Claude Vision extrahiert die Daten</p>
            </div>
          )}

          {/* ── Review ── */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-green-500 text-lg">✓</span>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Daten erkannt — bitte prüfen und ggf. korrigieren:
                </p>
              </div>

              {/* Doc-Type Toggle */}
              <div className="flex gap-2 flex-wrap">
                {(['mandant', 'arbeitnehmer', 'unbekannt'] as const).map(t => (
                  <button key={t}
                    onClick={() => { setForm(prev => ({ ...prev, doc_type: t })); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      form.doc_type === t
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-blue-400'
                    }`}>
                    {t === 'mandant' ? '🏢 Mandant' : t === 'arbeitnehmer' ? '👤 Arbeitnehmer' : '❓ Unbekannt'}
                  </button>
                ))}
              </div>

              {/* Mandant-Felder */}
              {form.doc_type === 'mandant' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Firmenname" value={form.firma}      onChange={set('firma')}      placeholder="Firmenbezeichnung" />
                    <EditField label="Kurzzeichen" value={form.kurz_code} onChange={set('kurz_code')} placeholder="z.B. RAM" />
                  </div>
                  <EditField label="Straße" value={form.strasse} onChange={set('strasse')} placeholder="Straße + Hausnummer" />
                  <div className="grid grid-cols-3 gap-3">
                    <EditField label="PLZ"  value={form.plz} onChange={set('plz')} placeholder="8990" />
                    <div className="col-span-2">
                      <EditField label="Ort" value={form.ort} onChange={set('ort')} placeholder="Bad Aussee" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Land"    value={form.land}    onChange={set('land')}    placeholder="Österreich" />
                    <EditField label="Branche" value={form.branche} onChange={set('branche')} placeholder="Metallerzeugung" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Telefon" value={form.telefon} onChange={set('telefon')} placeholder="+43 …" />
                    <EditField label="E-Mail"  value={form.email}   onChange={set('email')}   placeholder="info@firma.at" />
                  </div>

                  {/* Kontaktperson — nur wenn vorhanden oder user tippt rein */}
                  <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Kontaktperson (optional)
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <EditField label="Name"     value={form.ansprechperson}       onChange={set('ansprechperson')}       placeholder="Vor- und Nachname" />
                      <EditField label="Funktion" value={form.ansprechperson_titel} onChange={set('ansprechperson_titel')} placeholder="Geschäftsführer" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <EditField label="E-Mail"  value={form.ansprechperson_email}   onChange={set('ansprechperson_email')}   placeholder="person@firma.at" />
                      <EditField label="Telefon" value={form.ansprechperson_telefon} onChange={set('ansprechperson_telefon')} placeholder="+43 …" />
                    </div>
                  </div>
                </div>
              )}

              {/* Arbeitnehmer-Felder */}
              {form.doc_type === 'arbeitnehmer' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Vorname"  value={form.person_vorname}  onChange={set('person_vorname')}  placeholder="Vorname" />
                    <EditField label="Nachname" value={form.person_nachname} onChange={set('person_nachname')} placeholder="Nachname" />
                  </div>
                  <EditField label="Geburtsdatum" value={form.person_geburtsdatum}
                    onChange={set('person_geburtsdatum')} placeholder="TT.MM.JJJJ" />
                </div>
              )}

              {/* KI-Notizen */}
              {form.notizen !== '' && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Weitere Infos (KI)
                  </p>
                  <p className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg leading-relaxed">
                    {form.notizen}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Error ── */}
          {step === 'error' && (
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/40 rounded-xl p-4 space-y-1">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">Fehler</p>
              <p className="text-sm text-red-600 dark:text-red-300">{errorMsg}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex gap-2 justify-end flex-shrink-0">
          {step === 'idle' && (
            <button onClick={onClose} className="btn-secondary text-sm">Abbrechen</button>
          )}
          {step === 'dsgvo' && (
            <>
              <button onClick={reset} className="btn-secondary text-sm">Zurück</button>
              <button onClick={() => { void handleProcess(); }} className="btn-primary text-sm">
                Ja, Dokument analysieren
              </button>
            </>
          )}
          {step === 'processing' && (
            <button onClick={onClose} className="btn-secondary text-sm">Abbrechen</button>
          )}
          {step === 'review' && (
            <>
              <button onClick={reset} className="btn-secondary text-sm">Neues Dokument</button>
              <button onClick={handleConfirm} className="btn-primary text-sm">
                Daten übernehmen
              </button>
            </>
          )}
          {step === 'error' && (
            <>
              <button onClick={reset} className="btn-secondary text-sm">Zurück</button>
              <button onClick={onClose} className="btn-secondary text-sm">Schließen</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
